import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminDashboard, adminSendDaily, adminRegenerateToday, setAdminToken, hasAdminToken, clearAdminToken } from "@/lib/api";
import Loading from "@/components/Loading";
import { toast } from "sonner";
import { ShieldCheck, LogOut, Send, Mail, Users, BookOpen, Clock, Lock, Sparkles, RotateCw } from "lucide-react";

export default function Admin() {
    const [authed, setAuthed] = useState(hasAdminToken());
    const [tokenInput, setTokenInput] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [regen, setRegen] = useState(false);
    const navigate = useNavigate();

    async function fetchDashboard() {
        setLoading(true);
        try {
            const d = await adminDashboard();
            setData(d);
            setAuthed(true);
        } catch (e) {
            if (e?.response?.status === 401) {
                clearAdminToken();
                setAuthed(false);
                toast.error("Invalid admin token.");
            } else {
                toast.error("Failed to load dashboard.");
            }
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (authed) fetchDashboard();
        // eslint-disable-next-line
    }, []);

    function handleLogin(e) {
        e.preventDefault();
        if (!tokenInput.trim()) return;
        setAdminToken(tokenInput.trim());
        setAuthed(true);
        fetchDashboard();
    }

    function handleLogout() {
        clearAdminToken();
        setAuthed(false);
        setData(null);
        setTokenInput("");
        navigate("/");
    }

    async function handleSend() {
        if (!confirm("Send today's lesson to all active subscribers right now?")) return;
        setSending(true);
        try {
            const res = await adminSendDaily();
            toast.success(`Sent to ${res.sent} subscriber${res.sent === 1 ? "" : "s"}` + (res.failed ? ` (${res.failed} failed)` : ""));
            fetchDashboard();
        } catch (e) {
            toast.error("Send failed: " + (e?.response?.data?.detail || e.message));
        } finally {
            setSending(false);
        }
    }

    async function handleRegen() {
        if (!confirm("Regenerate today's lesson? This will replace today's existing lesson with a fresh AI-generated one (ESV-strict). Cannot be undone.")) return;
        setRegen(true);
        try {
            const res = await adminRegenerateToday();
            toast.success(`Regenerated: ${res.title}`, { description: `Key verse: ${res.key_verse}` });
            fetchDashboard();
        } catch (e) {
            toast.error("Regenerate failed: " + (e?.response?.data?.detail || e.message));
        } finally {
            setRegen(false);
        }
    }

    if (!authed) {
        return (
            <div className="max-w-md mx-auto px-5 py-20" data-testid="admin-login">
                <div className="card-sticker bg-white p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-coral border-2 border-ink">
                            <Lock className="w-6 h-6 text-white" strokeWidth={2.5} />
                        </span>
                        <div>
                            <h1 className="font-heading text-2xl font-bold">Admin</h1>
                            <p className="text-sm text-ink/60">Token required</p>
                        </div>
                    </div>
                    <form onSubmit={handleLogin} className="space-y-3">
                        <input
                            type="password"
                            value={tokenInput}
                            onChange={(e) => setTokenInput(e.target.value)}
                            placeholder="Admin token"
                            className="w-full border-2 border-ink rounded-full px-4 py-3 font-semibold focus:outline-none focus:ring-4 focus:ring-sky"
                            data-testid="admin-token-input"
                            autoFocus
                        />
                        <button type="submit" className="btn-sticker bg-sun text-ink w-full" data-testid="admin-login-btn">
                            <ShieldCheck className="w-5 h-5 mr-2" strokeWidth={2.5} /> Sign in
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    if (loading || !data) return <Loading label="Loading dashboard..." />;

    const { subscribers, lessons, recent_subscribers = [], recent_email_runs = [], schedule } = data;

    return (
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10" data-testid="admin-dashboard">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Admin dashboard</h1>
                    <p className="text-ink/60">2nd Mile Kids · {schedule.sender}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button onClick={handleRegen} disabled={regen} className="btn-sticker bg-sun text-ink text-sm" data-testid="regen-today-btn">
                        <RotateCw className={`w-4 h-4 mr-2 ${regen ? "animate-spin" : ""}`} strokeWidth={2.5} />
                        {regen ? "Generating..." : "Regenerate today"}
                    </button>
                    <button onClick={handleSend} disabled={sending} className="btn-sticker bg-coral text-white text-sm" data-testid="send-now-btn">
                        <Send className="w-4 h-4 mr-2" strokeWidth={2.5} />
                        {sending ? "Sending..." : "Send today's lesson"}
                    </button>
                    <button onClick={handleLogout} className="btn-sticker bg-white text-ink text-sm" data-testid="admin-logout-btn">
                        <LogOut className="w-4 h-4 mr-2" strokeWidth={2.5} /> Sign out
                    </button>
                </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                <StatCard icon={Users} color="bg-mint" label="Active subscribers" value={subscribers.active} />
                <StatCard icon={Mail} color="bg-coral" label="Inactive" value={subscribers.inactive} dark />
                <StatCard icon={BookOpen} color="bg-sun" label="Lessons in archive" value={lessons.total} />
                <StatCard icon={Clock} color="bg-sky" label={`Daily send`} value={`${String(schedule.hour).padStart(2, "0")}:00`} sub={schedule.timezone} dark />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <section className="card-sticker bg-white p-6">
                    <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-coral" strokeWidth={2.5} /> Recent subscribers
                    </h2>
                    {recent_subscribers.length === 0 ? (
                        <p className="text-ink/60">No subscribers yet.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left border-b-2 border-ink">
                                        <th className="py-2 pr-3">Email</th>
                                        <th className="py-2 pr-3">Refs</th>
                                        <th className="py-2">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recent_subscribers.map((s, i) => (
                                        <tr key={i} className="border-b border-ink/10" data-testid={`sub-row-${i}`}>
                                            <td className="py-2 pr-3 break-all">{s.email}</td>
                                            <td className="py-2 pr-3 font-mono">{s.referral_count || 0}/3 {s.unlocked_family_pack ? "🎁" : ""}</td>
                                            <td className="py-2">{s.active ? <span className="pill bg-mint">active</span> : <span className="pill bg-coral text-white">inactive</span>}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>

                <section className="card-sticker bg-white p-6">
                    <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                        <Send className="w-5 h-5 text-sky" strokeWidth={2.5} /> Recent email runs
                    </h2>
                    {recent_email_runs.length === 0 ? (
                        <p className="text-ink/60">No emails sent yet.</p>
                    ) : (
                        <ul className="space-y-3">
                            {recent_email_runs.map((r, i) => (
                                <li key={i} className="flex justify-between gap-3 text-sm pb-3 border-b border-ink/10" data-testid={`run-row-${i}`}>
                                    <div className="min-w-0">
                                        <p className="font-semibold truncate">{r.lesson_title}</p>
                                        <p className="text-ink/60 text-xs">{new Date(r.ran_at).toLocaleString()} · {r.trigger}</p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="font-mono">✅ {r.sent} · ❌ {r.failed}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>

            {lessons.today && (
                <section className="card-sticker bg-sun/40 p-6 mt-6">
                    <p className="font-accent text-2xl text-coral">Today's lesson</p>
                    <p className="font-heading text-xl font-bold mt-1">{lessons.today.title}</p>
                    <p className="text-ink/70 text-sm">{lessons.today.theme} · {lessons.today.bible_reference}</p>
                </section>
            )}
        </div>
    );
}

function StatCard({ icon: Icon, color, label, value, sub, dark }) {
    return (
        <div className="card-sticker bg-white p-5">
            <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl border-2 border-ink ${color} mb-3`}>
                <Icon className={`w-5 h-5 ${dark ? "text-white" : "text-ink"}`} strokeWidth={2.5} />
            </div>
            <p className="text-xs uppercase tracking-widest font-bold text-ink/60">{label}</p>
            <p className="font-heading text-3xl font-bold mt-1">{value}</p>
            {sub && <p className="text-xs text-ink/60">{sub}</p>}
        </div>
    );
}
